import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, RefreshCw, AlertTriangle, ShieldCheck, Star } from "lucide-react";

const FundingAdminTab = () => {
  const qc = useQueryClient();
  const [verifying, setVerifying] = useState(false);

  const { data: funders } = useQuery({
    queryKey: ["admin-funders"],
    queryFn: async () => {
      const { data } = await supabase.from("funders").select("*").order("name");
      return data ?? [];
    },
  });

  const { data: programs } = useQuery({
    queryKey: ["admin-programs"],
    queryFn: async () => {
      const { data } = await supabase
        .from("grant_programs")
        .select("id, title, verified, program_url, funders:funder_id(name, slug)")
        .order("title");
      return data ?? [];
    },
  });

  const { data: broken } = useQuery({
    queryKey: ["admin-broken-links"],
    queryFn: async () => {
      const { data } = await supabase
        .from("link_health")
        .select("*")
        .gt("failure_count", 0)
        .order("last_checked_at", { ascending: false });
      return data ?? [];
    },
  });

  const sponsorMut = useMutation({
    mutationFn: async ({ id, tier, until }: { id: string; tier: string | null; until: string | null }) => {
      const { error } = await supabase
        .from("funders")
        .update({ sponsor_tier: tier, sponsor_until: until })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Sponsor settings updated");
      qc.invalidateQueries({ queryKey: ["admin-funders"] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  const SOURCE_URL_COLUMNS: Record<string, string[]> = {
    funders: ["website", "source_url"],
    grant_programs: ["program_url"],
  };

  const removeMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("link_health").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Broken link removed");
      qc.invalidateQueries({ queryKey: ["admin-broken-links"] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  const fixUrl = async (b: any) => {
    const next = window.prompt("Update the URL on the source record:", b.url);
    if (!next || next.trim() === "" || next === b.url) return;
    const cols = SOURCE_URL_COLUMNS[b.source_table] ?? [];
    if (!b.source_id || cols.length === 0) {
      toast.error(`Cannot auto-fix links from "${b.source_table}". Edit the source record directly.`);
      return;
    }
    try {
      let updated = false;
      for (const col of cols) {
        const { data, error } = await supabase
          .from(b.source_table)
          .update({ [col]: next.trim() })
          .eq("id", b.source_id)
          .eq(col, b.url)
          .select("id");
        if (error) throw error;
        if (data && data.length > 0) updated = true;
      }
      if (!updated) {
        toast.error("Source URL no longer matches — skipped.");
        return;
      }
      await supabase.from("link_health").delete().eq("id", b.id);
      toast.success("URL updated. Re-run verification to re-check.");
      qc.invalidateQueries({ queryKey: ["admin-broken-links"] });
      qc.invalidateQueries({ queryKey: ["admin-funders"] });
      qc.invalidateQueries({ queryKey: ["admin-programs"] });
    } catch (e: any) {
      toast.error(e.message ?? "Failed to update URL");
    }
  };

  const runVerify = async () => {
    setVerifying(true);
    try {
      const { data, error } = await supabase.functions.invoke("verify-funding-links", { body: {} });
      if (error) throw error;
      toast.success(`Checked ${data?.checked ?? 0} links · ${data?.broken ?? 0} broken`);
      qc.invalidateQueries({ queryKey: ["admin-broken-links"] });
    } catch (e: any) {
      toast.error(e.message ?? "Verify failed");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-semibold">Funding</h2>
        <Button onClick={runVerify} disabled={verifying} variant="outline" className="gap-2">
          {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Run link verification
        </Button>
      </div>

      <Tabs defaultValue="funders">
        <TabsList>
          <TabsTrigger value="funders">Funders ({funders?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="programs">Programs ({programs?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="broken">Broken links ({broken?.length ?? 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="funders" className="mt-4 space-y-2">
          {funders?.map((f: any) => (
            <div key={f.id} className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-sm">{f.name}</h3>
                    {f.verified && <Badge variant="outline" className="gap-1 text-[10px] bg-emerald-500/10 text-emerald-600 border-emerald-500/30"><ShieldCheck className="h-3 w-3" />Verified</Badge>}
                    {f.sponsor_tier && <Badge variant="outline" className="gap-1 text-[10px] bg-amber-500/10 text-amber-600 border-amber-500/30"><Star className="h-3 w-3" />{f.sponsor_tier}</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground">{f.funder_type} · {f.slug}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3 items-center">
                <label className="text-[11px] text-muted-foreground">Sponsor tier:</label>
                <select
                  className="text-xs bg-background border border-border rounded px-2 py-1"
                  value={f.sponsor_tier ?? ""}
                  onChange={(e) => sponsorMut.mutate({ id: f.id, tier: e.target.value || null, until: f.sponsor_until })}
                >
                  <option value="">(none)</option>
                  <option value="featured">featured</option>
                  <option value="premium">premium</option>
                </select>
                <label className="text-[11px] text-muted-foreground ml-2">Until:</label>
                <input
                  type="date"
                  className="text-xs bg-background border border-border rounded px-2 py-1"
                  value={f.sponsor_until ? f.sponsor_until.substring(0, 10) : ""}
                  onChange={(e) => sponsorMut.mutate({ id: f.id, tier: f.sponsor_tier, until: e.target.value ? new Date(e.target.value).toISOString() : null })}
                />
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="programs" className="mt-4 space-y-2">
          {programs?.map((p: any) => (
            <div key={p.id} className="bg-card border border-border rounded-lg p-3 flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-medium">{p.title}</div>
                <div className="text-xs text-muted-foreground">{p.funders?.name ?? "Unlinked"}</div>
              </div>
              {p.verified ? (
                <Badge variant="outline" className="gap-1 text-[10px] bg-emerald-500/10 text-emerald-600 border-emerald-500/30"><ShieldCheck className="h-3 w-3" />Verified</Badge>
              ) : (
                <Badge variant="outline" className="text-[10px]">Unverified</Badge>
              )}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="broken" className="mt-4 space-y-2">
          {broken && broken.length > 0 ? broken.map((b: any) => (
            <div key={b.id} className="bg-card border border-border rounded-lg p-3 flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0" />
                  <a href={b.url} target="_blank" rel="noopener noreferrer" className="text-xs font-mono break-all hover:underline">{b.url}</a>
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {b.source_table} · status {b.last_status ?? "n/a"} · failures {b.failure_count} · last checked {b.last_checked_at ? new Date(b.last_checked_at).toLocaleString() : "—"}
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" variant="outline" onClick={() => fixUrl(b)}>Fix URL</Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                  onClick={() => {
                    if (window.confirm("Remove this broken link entry?")) removeMut.mutate(b.id);
                  }}
                >
                  Remove
                </Button>
              </div>
            </div>
          )) : (
            <p className="text-sm text-muted-foreground text-center py-8">No broken links detected. Run a verification to refresh.</p>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FundingAdminTab;